<template>
  <v-card class="mb-4">
    <!-- Collapsed by default: most of a booking day never touches a route, and
         the open board pushed everything under it off the screen. The header
         still says how many routes are taking bookings and, in amber, how many
         bookings have no route yet -- the one thing on this board that needs a
         person, so folding it away must not hide it. เปิดเส้นทาง stays in the
         header and .stop keeps it from toggling the board. -->
    <v-toolbar color="deep-purple" dark dense flat class="board-toggle" @click="expanded = !expanded">
      <v-icon class="mr-2">mdi-map-marker-distance</v-icon>
      <v-toolbar-title>เส้นทางนอกพื้นที่</v-toolbar-title>
      <v-chip x-small color="white" outlined class="ml-3">เปิดรับ {{ openCount }}</v-chip>
      <v-chip v-if="strays.length" x-small color="amber darken-2" dark class="ml-1">
        ยังไม่มีเส้นทาง {{ strays.length }}
      </v-chip>
      <v-spacer />
      <v-btn v-if="canManage" small color="white" outlined class="mr-2" @click.stop="$emit('open')">
        <v-icon left small>mdi-plus</v-icon>เปิดเส้นทาง
      </v-btn>
      <v-icon>{{ expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
    </v-toolbar>

    <v-expand-transition>
    <v-card-text v-show="expanded">
      <div v-if="!trips.length && !strays.length" class="grey--text text-center py-4">
        ยังไม่มีเส้นทางนอกพื้นที่
      </div>

      <v-card v-for="t in trips" :key="t.id" outlined class="mb-2">
        <v-card-text class="py-2">
          <div class="d-flex align-center flex-wrap">
            <strong class="mr-2">{{ t.date }}</strong>
            <!-- ✕ only while there is another province to keep: the last one
                 is ยกเลิกเส้นทาง's job. Whether anyone is booked there is the
                 transaction's to decide, so the ✕ is offered regardless. -->
            <v-chip v-for="p in t.provinces" :key="p" x-small color="deep-purple" dark class="mr-1"
                    :close="canManage && !t.cancelled && (t.provinces || []).length > 1"
                    @click:close="$emit('removeProvince', { trip: t, province: p })">
              {{ p }}
            </v-chip>
            <v-spacer />
            <!-- The number delivery is actually managing: how full the truck is.
                 Amber near the top rather than green, because a route one seat
                 from closing is a route sales should be told about. -->
            <v-chip small :color="fillColor(t)" dark class="mr-1">
              {{ t.count || 0 }}/{{ t.capacity || 0 }} งาน
            </v-chip>
            <!-- ยกเลิกแล้ว first: a cancelled route is also closed, so reading
                 the status alone would show it as ปิดรับ beside a เปิดรับอีกครั้ง
                 button the transaction now refuses. -->
            <v-chip small :color="t.cancelled ? 'grey' : (t.status === 'open' ? 'green' : 'grey')" dark>
              {{ t.cancelled ? 'ยกเลิกแล้ว' : (t.status === 'open' ? 'เปิดรับ' : (t.autoClosed ? 'เต็ม' : 'ปิดรับ')) }}
            </v-chip>
          </div>

          <div class="caption grey--text mt-1">
            จองรถ {{ t.cars || 1 }} คัน · เปิดโดย {{ $personName(t.openedBy) || '-' }}
            <span v-if="t.regNum"> · ส่งเข้าคิวจัดรถแล้ว ({{ t.regNum }})</span>
            <span v-if="t.regNum"> · {{ t.days === 2 ? '2 วัน' : '1 วัน' }}
              <a @click.stop="$emit('days', { tripId: t.id, days: t.days === 2 ? 1 : 2 })">แก้จำนวนวัน</a></span>
          </div>

          <div v-if="canManage" class="mt-2">
            <!-- ดูรายการจอง stays on a cancelled route: the history is still
                 worth reading. Nothing else is -- there is nothing left to do
                 to it, and every other action would be refused. -->
            <v-btn x-small text color="deep-purple" @click="$emit('pick', t.id)">ดูรายการจอง</v-btn>
            <template v-if="!t.cancelled">
              <v-btn v-if="t.status === 'open'" x-small text color="error"
                     @click="$emit('close', t.id)">ปิดรับ</v-btn>
              <v-btn v-else x-small text color="success" @click="$emit('reopen', t.id)">เปิดรับอีกครั้ง</v-btn>
              <v-btn x-small text @click="editCapacity(t)">แก้จำนวนงาน</v-btn>
              <!-- The same drive reaching one more province. The picker needs
                   zonedv.json, which the page loads, so the page draws it. -->
              <v-btn x-small text color="deep-purple" @click="$emit('addProvince', t)">
                <v-icon left x-small>mdi-plus</v-icon>จังหวัด
              </v-btn>
              <!-- The step that was missing: without it the only way to dispatch
                   an out-of-area job was to retype it into carBooking, and a
                   retyped job loses its พิกัด and จุดสังเกต. Offered only while
                   the route has work on it. Re-pressing after a late booking
                   joins sends just that one. -->
              <v-btn v-if="t.count" x-small text color="deep-purple"
                     @click="openSend(t)">
                {{ t.regNum ? 'ส่งเพิ่มเข้าคิวจัดรถ' : 'ส่งเข้าคิวจัดรถ' }}
              </v-btn>
              <!-- The only thing that gives a truck back, and offered only while
                   the route is empty: cancelling one with bookings on it would
                   take the truck out from under a customer who has a date. -->
              <v-btn v-if="!(t.count || 0)" x-small text color="error"
                     @click="$emit('cancelTrip', t.id)">ยกเลิกเส้นทาง</v-btn>
            </template>
          </div>
        </v-card-text>
      </v-card>

      <!-- Bookings made before routes existed (PR #35). Named rather than
           hidden: they are real customers with real dates, and the only thing
           that will attach them to a route is someone seeing them here. -->
      <template v-if="strays.length">
        <div class="subtitle-2 mt-4 mb-2">ยังไม่มีเส้นทาง ({{ strays.length }})</div>
        <v-alert dense text type="info" class="caption mb-2">
          รายการเหล่านี้จองไว้ก่อนมีระบบเส้นทาง เลือกเส้นทางให้แต่ละรายการเพื่อให้นับรวมกับรถที่จองไว้
        </v-alert>
        <v-card v-for="b in strays" :key="b.id" outlined class="mb-1">
          <v-card-text class="py-2 d-flex align-center flex-wrap">
            <span class="mr-2">{{ b.date }}</span>
            <strong class="mr-2">{{ b.name || b.custName || b.soNo || '—' }}</strong>
            <span class="caption grey--text">{{ b.location || b.address || '' }}</span>
            <v-spacer />
            <v-select v-if="canManage" :items="tripOptions(b)" dense hide-details
                      label="เลือกเส้นทาง" style="max-width: 260px"
                      @change="id => $emit('attach', { bookingId: b.id, tripId: id })" />
          </v-card-text>
        </v-card>
      </template>
    </v-card-text>
    </v-expand-transition>

    <v-dialog v-model="sendDialog.open" max-width="420">
      <v-card>
        <v-toolbar color="deep-purple" dark dense flat>
          <v-toolbar-title class="subtitle-1">ส่งเข้าคิวจัดรถ</v-toolbar-title>
        </v-toolbar>
        <v-card-text class="pt-4">
          <div class="mb-3">
            {{ sendDialog.provinces }} · {{ sendDialog.date }} · {{ sendDialog.count }} งาน
          </div>
          <!-- The plate is chosen here and not when the route was opened: a
               route opened three weeks out has no business claiming a truck
               that happens to be free on the day. -->
          <v-select v-model="sendDialog.regNum" :items="plates" label="เลือกรถ"
                    color="deep-purple" hide-details="auto" />
          <v-btn-toggle v-model="sendDialog.days" mandatory dense class="mt-2">
            <v-btn :value="1" small>1 วัน</v-btn>
            <v-btn :value="2" small>2 วัน (ค้างคืน)</v-btn>
          </v-btn-toggle>
          <v-alert dense text type="info" class="caption mt-3 mb-0">
            งานจะไปอยู่ในหน้าคิวรถเล็กของรถคันนี้ พร้อมพิกัดและจุดสังเกตที่บันทึกไว้
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="sendDialog.open = false">ยกเลิก</v-btn>
          <v-btn color="deep-purple" dark :disabled="!sendDialog.regNum"
                 :loading="sendDialog.saving" @click="confirmSend">ส่ง</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="capDialog.open" max-width="360">
      <v-card>
        <v-toolbar color="deep-purple" dark dense flat>
          <v-toolbar-title class="subtitle-1">แก้จำนวนงานต่อเส้นทาง</v-toolbar-title>
        </v-toolbar>
        <v-card-text class="pt-4">
          <v-text-field v-model.number="capDialog.capacity" label="จำนวนงาน" type="number" min="1"
                        color="deep-purple" hide-details="auto" />
          <div class="caption grey--text mt-2">
            ลดต่ำกว่าจำนวนที่จองไว้แล้วได้ เส้นทางจะปิดรับทันที
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="capDialog.open = false">ยกเลิก</v-btn>
          <v-btn color="deep-purple" dark @click="saveCapacity">บันทึก</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
// Presentational only: it renders trips and emits what the operator asked for.
// Every Firestore write and every rule lives where it already does --
// bookingService and bookingRules -- so this file can be read in one sitting
// and bookingDate.vue does not grow another two hundred lines.
import R from '@/components/bookingRules'

export default {
  name: 'tripBoard',
  props: {
    trips: { type: Array, default: () => [] },
    strays: { type: Array, default: () => [] },
    canManage: { type: Boolean, default: false },
    // Plate strings. Supplied by the page rather than read here, because this
    // component touches no Firestore -- fleet.js is the page's to call.
    plates: { type: Array, default: () => [] }
  },
  data () {
    return {
      expanded: false,
      capDialog: { open: false, tripId: '', capacity: 1 },
      sendDialog: { open: false, saving: false, tripId: '', regNum: '', days: 1, provinces: '', date: '', count: 0 }
    }
  },
  computed: {
    openCount () {
      return this.trips.filter(t => !t.cancelled && t.status === 'open').length
    }
  },
  methods: {
    fillColor (t) {
      // A cancelled route is grey whatever its seat count says. It should never
      // reach this component -- subscribeTrips drops them -- but the two chips
      // must not be able to contradict each other if one ever does: green next
      // to ยกเลิกแล้ว reads as a live route, which is exactly the confusion
      // that put this line here.
      if (t.cancelled) return 'grey'
      const left = Math.max(0, (t.capacity || 0) - (t.count || 0))
      if (left === 0) return 'grey darken-1'
      return left <= 1 ? 'amber darken-2' : 'green'
    },
    // Only routes that could actually take this booking: same date, still open,
    // and covering its province. Offering a route that would be refused turns a
    // one-click fix into a puzzle.
    tripOptions (booking) {
      // R.provinceOf, not a local split on booking.location: this codebase
      // deliberately refuses to have two address parsers that can disagree
      // about where an address is (see the note on serviceArea.js not
      // exporting parseAddress). This component holds no rules of its own,
      // but it can still call the one shared reader.
      const province = R.provinceOf(booking)
      return this.trips
        .filter(t => t.date === booking.date && t.status === 'open' &&
          (t.provinces || []).indexOf(province) !== -1)
        .map(t => ({ text: `${t.provinces.join(', ')} · ${t.count || 0}/${t.capacity || 0}`, value: t.id }))
    },
    openSend (t) {
      this.sendDialog = {
        open: true,
        saving: false,
        tripId: t.id,
        // Pre-filled when the route has already gone out once: the follow-up
        // booking almost always belongs on the same truck, and retyping the
        // plate is how it ends up on a different one.
        regNum: t.regNum || '',
        // A route already sent keeps its days: a different value here would
        // change the route while earlier jobs keep their old tripEnd.
        // Changing it is แก้จำนวนวัน.
        days: t.days || 1,
        provinces: (t.provinces || []).join(', '),
        date: t.date,
        count: t.count || 0
      }
    },
    confirmSend () {
      this.sendDialog.saving = true
      this.$emit('send', { tripId: this.sendDialog.tripId, regNum: this.sendDialog.regNum, days: this.sendDialog.days })
      this.sendDialog.open = false
      this.sendDialog.saving = false
    },
    editCapacity (t) {
      this.capDialog = { open: true, tripId: t.id, capacity: t.capacity || 1 }
    },
    saveCapacity () {
      this.$emit('capacity', { tripId: this.capDialog.tripId, capacity: this.capDialog.capacity })
      this.capDialog.open = false
    }
  }
}
</script>

<style scoped>
.board-toggle {
  cursor: pointer;
}
</style>
